import { useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import {
  BoldIcon,
  ItalicIcon,
  ListBulletIcon,
  NumberedListIcon,
} from '@heroicons/react/24/outline';
import { Button } from '@theraply/ui';
import clsx from 'clsx';

interface NoteEditorProps {
  content: string;
  onChange: (content: string) => void;
  onSave?: () => void;
  isSaving?: boolean;
  lastSaved?: Date | null;
  placeholder?: string;
  readOnly?: boolean;
}

export function NoteEditor({
  content,
  onChange,
  onSave,
  isSaving = false,
  lastSaved,
  placeholder = 'Start writing your session notes...',
  readOnly = false,
}: NoteEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Placeholder.configure({
        placeholder,
      }),
    ],
    content,
    editable: !readOnly,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Update editor content when content prop changes externally
  useEffect(() => {
    if (editor && content !== editor.getHTML()) {
      editor.commands.setContent(content);
    }
  }, [content, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      {!readOnly && (
        <div className="border-b border-gray-200 bg-white p-2 flex items-center gap-2">
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBold().run()}
              className={clsx(
                'p-2 rounded hover:bg-gray-100',
                editor.isActive('bold') ? 'bg-gray-200' : ''
              )}
              title="Bold"
            >
              <BoldIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleItalic().run()}
              className={clsx(
                'p-2 rounded hover:bg-gray-100',
                editor.isActive('italic') ? 'bg-gray-200' : ''
              )}
              title="Italic"
            >
              <ItalicIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="h-6 w-px bg-gray-300" />

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
              className={clsx(
                'px-3 py-1 rounded hover:bg-gray-100 font-semibold',
                editor.isActive('heading', { level: 1 }) ? 'bg-gray-200' : ''
              )}
              title="Heading 1"
            >
              H1
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
              className={clsx(
                'px-3 py-1 rounded hover:bg-gray-100 font-semibold',
                editor.isActive('heading', { level: 2 }) ? 'bg-gray-200' : ''
              )}
              title="Heading 2"
            >
              H2
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
              className={clsx(
                'px-3 py-1 rounded hover:bg-gray-100 font-semibold',
                editor.isActive('heading', { level: 3 }) ? 'bg-gray-200' : ''
              )}
              title="Heading 3"
            >
              H3
            </button>
          </div>

          <div className="h-6 w-px bg-gray-300" />

          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleBulletList().run()}
              className={clsx(
                'p-2 rounded hover:bg-gray-100',
                editor.isActive('bulletList') ? 'bg-gray-200' : ''
              )}
              title="Bullet List"
            >
              <ListBulletIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => editor.chain().focus().toggleOrderedList().run()}
              className={clsx(
                'p-2 rounded hover:bg-gray-100',
                editor.isActive('orderedList') ? 'bg-gray-200' : ''
              )}
              title="Numbered List"
            >
              <NumberedListIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1" />

          {/* Save Status */}
          <div className="flex items-center gap-3">
            {isSaving && (
              <span className="text-sm text-gray-500 flex items-center gap-2">
                <span className="inline-block h-4 w-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                Encrypting and saving...
              </span>
            )}
            {!isSaving && lastSaved && (
              <span className="text-sm text-gray-500">
                Saved {formatLastSaved(lastSaved)}
              </span>
            )}
            {onSave && (
              <Button
                variant="primary"
                size="sm"
                onClick={onSave}
                disabled={isSaving}
              >
                Save Now
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Editor Content */}
      <div className="flex-1 overflow-auto bg-white">
        <EditorContent
          editor={editor}
          className={clsx(
            'prose prose-sm max-w-none p-6',
            'focus:outline-none',
            'min-h-full'
          )}
        />
      </div>

      {/* Character Count */}
      {!readOnly && (
        <div className="border-t border-gray-200 bg-gray-50 px-4 py-2 text-xs text-gray-500">
          {editor.storage.characterCount?.characters() || 0} characters
        </div>
      )}
    </div>
  );
}

function formatLastSaved(date: Date): string {
  const now = new Date();
  const diffSeconds = Math.floor((now.getTime() - date.getTime()) / 1000);

  if (diffSeconds < 60) {
    return 'just now';
  } else if (diffSeconds < 3600) {
    const minutes = Math.floor(diffSeconds / 60);
    return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'} ago`;
  } else {
    return date.toLocaleTimeString();
  }
}
